import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney } from '@/lib/money';
import type {
  AlertRule,
  AlertRuleListResponse,
  Notification,
  NotificationListResponse,
  TriggerInfo,
} from './types';
import { SEVERITY_STYLE } from './types';
import { AlertRuleForm } from './AlertRuleForm';

/**
 * Alertas — RF-ALE-01, RF-ALE-02.
 *
 * Duas abas porque são duas perguntas diferentes: "o que o sistema está me
 * dizendo agora" e "sobre o que eu quero ser avisado". Juntá-las numa tela só
 * faria a configuração competir com o aviso justamente no momento em que o
 * aviso importa.
 */
export function AlertsPage({ selectedIds }: { selectedIds: number[] }) {
  const [tab, setTab] = useState<'caixa' | 'regras'>('caixa');

  return (
    <div className="mx-auto max-w-5xl">
      <h1 className="text-xl font-semibold tracking-tight">Alertas</h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        O sistema avalia as regras todos os dias às 07:00 e avisa o que precisa da sua atenção.
      </p>

      <div className="mt-4 flex rounded-lg border border-slate-300 p-0.5 dark:border-slate-700">
        {([['caixa', 'Caixa de entrada'], ['regras', 'Regras']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              tab === id
                ? 'bg-indigo-600 text-white'
                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'caixa' ? <Inbox /> : <RulesPanel selectedIds={selectedIds} />}
    </div>
  );
}

// -------------------------------------------------------------------
// Caixa de entrada
// -------------------------------------------------------------------

function Inbox() {
  const [onlyUnread, setOnlyUnread] = useState(false);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['notifications', 'inbox', onlyUnread],
    queryFn: () =>
      api.get<NotificationListResponse>(
        `/notifications?per_page=50${onlyUnread ? '&only_unread=1' : ''}`,
      ),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const dismiss = useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/dismiss`, {}),
    onSuccess: invalidate,
  });

  const markRead = useMutation({
    mutationFn: (id: number) => api.post(`/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const items = list.data?.data ?? [];

  return (
    <div className="mt-5">
      <label className="flex cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={onlyUnread}
          onChange={(e) => setOnlyUnread(e.target.checked)}
          className="rounded border-slate-300 text-indigo-600 dark:border-slate-600 focus:ring-indigo-500"
        />
        Só não lidos
      </label>

      {list.isPending ? (
        <div className="mt-3 animate-pulse space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="mt-3 rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
          {onlyUnread ? 'Nenhum alerta não lido.' : 'Nenhum alerta. Cadastre regras na aba ao lado.'}
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((n) => (
            <InboxRow
              key={n.id}
              notification={n}
              onRead={() => markRead.mutate(n.id)}
              onDismiss={() => dismiss.mutate(n.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InboxRow({
  notification,
  onRead,
  onDismiss,
}: {
  notification: Notification;
  onRead: () => void;
  onDismiss: () => void;
}) {
  const style = SEVERITY_STYLE[notification.severity];
  const isUnread = notification.read_at === null;

  return (
    <div
      className={`flex gap-3 rounded-xl border p-3.5 ${
        isUnread
          ? 'border-indigo-200 bg-indigo-50/40 dark:border-indigo-900/50 dark:bg-indigo-950/20'
          : 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900'
      }`}
    >
      <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${style.dot}`} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className={`text-sm ${isUnread ? 'font-medium' : ''}`}>{notification.title}</p>
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${style.chip}`}>
            {style.label}
          </span>
        </div>

        {notification.body && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{notification.body}</p>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-slate-400">
          <span>{new Date(notification.created_at).toLocaleString('pt-BR')}</span>
          {notification.amount && (
            <span className="font-medium text-slate-600 dark:text-slate-300">
              {formatMoney(notification.amount)}
            </span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-start gap-2 text-xs">
        {isUnread && (
          <button onClick={onRead} className="text-slate-400 hover:text-indigo-600">
            Marcar lido
          </button>
        )}
        <button onClick={onDismiss} className="text-slate-400 hover:text-rose-600">
          Dispensar
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Regras
// -------------------------------------------------------------------

function RulesPanel({ selectedIds }: { selectedIds: number[] }) {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AlertRule | null>(null);
  const queryClient = useQueryClient();

  const rules = useQuery({
    queryKey: ['alert-rules', selectedIds],
    queryFn: () => api.get<AlertRuleListResponse>('/alert-rules', { entityIds: selectedIds }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['alert-rules'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/alert-rules/${id}`, { entityIds: selectedIds }),
    onSuccess: invalidate,
  });

  const runNow = useMutation({
    mutationFn: () =>
      api.post<{ data: { notifications_created: number }; meta: { note: string } }>(
        '/alert-rules/run',
        {},
        { entityIds: selectedIds },
      ),
    onSuccess: (res) => {
      invalidate();
      alert(res.meta.note);
    },
    onError: (e) => alert(e instanceof ApiError ? e.message : 'Falha ao avaliar as regras.'),
  });

  const list = rules.data?.data ?? [];
  const triggers = rules.data?.meta.available_triggers ?? [];

  return (
    <div className="mt-5">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCreating(true)}
          className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
        >
          + Nova regra
        </button>

        <button
          onClick={() => runNow.mutate()}
          disabled={runNow.isPending || list.length === 0}
          className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm transition hover:border-indigo-500 hover:text-indigo-600 disabled:opacity-50 dark:border-slate-700"
          /* Repetir é seguro, e dizer isso remove o medo de clicar. */
          title="Pode ser repetido: um mesmo aviso não é criado duas vezes"
        >
          {runNow.isPending ? 'Avaliando…' : 'Avaliar agora'}
        </button>
      </div>

      {rules.isPending ? (
        <div className="mt-3 animate-pulse space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-slate-100 dark:bg-slate-800" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="mt-3 rounded-xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Nenhuma regra cadastrada — o sistema ainda não avisa nada.
          </p>
          <p className="mx-auto mt-2 max-w-md text-xs text-slate-400">
            Comece com <strong>Conta a pagar vencendo</strong> com 3 dias de antecedência. É a regra
            que evita o tipo mais caro de erro: pagar juros por esquecimento.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {list.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              triggers={triggers}
              onEdit={() => setEditing(rule)}
              onDelete={() => {
                if (confirm(`Excluir a regra "${rule.name}"? Ela deixa de avisar imediatamente.`)) {
                  remove.mutate(rule.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {(creating || editing) && (
        <AlertRuleForm
          rule={editing}
          triggers={triggers}
          selectedIds={selectedIds}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function RuleRow({
  rule,
  triggers,
  onEdit,
  onDelete,
}: {
  rule: AlertRule;
  triggers: TriggerInfo[];
  onEdit: () => void;
  onDelete: () => void;
}) {
  const trigger = triggers.find((t) => t.id === rule.trigger);
  const style = SEVERITY_STYLE[rule.severity];

  const param =
    rule.days_before !== null
      ? `${rule.days_before} dia${rule.days_before === 1 ? '' : 's'} antes`
      : rule.amount_threshold !== null
        ? `abaixo de ${formatMoney(rule.amount_threshold)}`
        : rule.percentage_threshold !== null
          ? `a partir de ${rule.percentage_threshold}%`
          : null;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5 dark:border-slate-800 dark:bg-slate-900">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${rule.active ? style.dot : 'bg-slate-300'}`} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium">{rule.name}</p>
          {!rule.active && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
              inativa
            </span>
          )}
        </div>

        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
          {trigger?.label ?? rule.trigger}
          {param && ` · ${param}`}
          {' · '}
          {rule.entity_name ?? 'todas as entidades'}
        </p>

        {/* Quantas vezes disparou responde "esta regra serve para alguma
            coisa?" melhor do que qualquer descrição. */}
        <p className="mt-0.5 text-[11px] text-slate-400">
          {rule.times_triggered === 0
            ? 'Ainda não disparou'
            : `${rule.times_triggered} aviso(s) · último em ${
                rule.last_triggered_at
                  ? new Date(rule.last_triggered_at).toLocaleDateString('pt-BR')
                  : '—'
              }`}
        </p>
      </div>

      <div className="flex shrink-0 gap-2 text-xs">
        <button onClick={onEdit} className="text-slate-500 dark:text-slate-400 hover:text-indigo-600">
          Editar
        </button>
        <button onClick={onDelete} className="text-slate-500 dark:text-slate-400 hover:text-rose-600">
          Excluir
        </button>
      </div>
    </div>
  );
}
